import { z } from 'zod';
import type { InterventionStatus } from './schemas/common.js';

/**
 * Vague 7 : **frise d'étapes du client** (`PortalViewOutput.steps`), même logique côté garage (fiche) et
 * côté client (portail). Étapes, dans l'ordre :
 * `received` → [`diagnostic` → `decision`] → [`waiting_parts`] → `work` → `ready` → `released`.
 * - `diagnostic` et `decision` n'apparaissent que si des travaux complémentaires ont été envoyés au client
 *   (au moins une version) ou si le statut est `awaiting_decision` ;
 * - `waiting_parts` n'apparaît que si le statut est `awaiting_parts`.
 * État : les étapes avant l'étape courante sont `done`, l'étape courante `current`, les suivantes `todo` ;
 * dossier clôturé (sorti) : tout est `done`.
 */

export const PORTAL_STEP_KEYS = ['received', 'diagnostic', 'decision', 'waiting_parts', 'work', 'ready', 'released'] as const;
export const PortalStepKey = z.enum(PORTAL_STEP_KEYS);
export type PortalStepKey = z.infer<typeof PortalStepKey>;
export const PortalStepState = z.enum(['done', 'current', 'todo']);
export type PortalStepState = z.infer<typeof PortalStepState>;

/** Libellé vu par le client et clé d'icône (l'interface la traduit en pictogramme). */
export const PORTAL_STEPS: Record<PortalStepKey, { label: string; icon: string }> = {
  received: { label: 'Véhicule reçu', icon: 'car' },
  diagnostic: { label: 'Diagnostic', icon: 'search' },
  decision: { label: 'Votre accord', icon: 'check-circle' },
  waiting_parts: { label: 'Pièces commandées', icon: 'package' },
  work: { label: 'Travaux', icon: 'wrench' },
  ready: { label: 'Prêt à récupérer', icon: 'key' },
  released: { label: 'Restitué', icon: 'flag' },
};

export const PortalStep = z.object({
  key: PortalStepKey,
  state: PortalStepState,
  label: z.string(),
  icon: z.string(),
});
export type PortalStep = z.infer<typeof PortalStep>;

const CURRENT_OF: Record<InterventionStatus, PortalStepKey> = {
  received: 'received',
  in_progress: 'work',
  awaiting_decision: 'decision',
  awaiting_parts: 'waiting_parts',
  ready: 'ready',
  closed: 'released',
};

export function buildPortalSteps(input: { status: InterventionStatus; hasSentComplement: boolean }): PortalStep[] {
  const withDecision = input.hasSentComplement || input.status === 'awaiting_decision';
  const keys = PORTAL_STEP_KEYS.filter((k) => {
    if (k === 'diagnostic' || k === 'decision') return withDecision;
    if (k === 'waiting_parts') return input.status === 'awaiting_parts';
    return true;
  });
  const currentIndex = keys.indexOf(CURRENT_OF[input.status]);
  const closed = input.status === 'closed';
  return keys.map((key, index) => ({
    key,
    state: closed || index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo',
    label: PORTAL_STEPS[key].label,
    icon: PORTAL_STEPS[key].icon,
  }));
}
